/**
 * Shared utilities for email template placeholder resolution.
 *
 * Used by both CustomerSendEmailModal (customers page, bulk mode) and
 * TransactionSendCopyInvoiceModal (transactions page, single mode).
 */

// ─── HTML / Variable helpers ─────────────────────────────────────────────────

export function stripHtml(html: string): string {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

export const convertVariablesToNodes = (html: string): string => {
  const normalized = html.replace(
    /<span[^>]*data-variable="([^"]+)"[^>]*>[^<]*<\/span>/g,
    "$1",
  );
  // Decode HTML-encoded angle brackets so both raw <<key>> and &lt;&lt;key&gt;&gt;
  // are converted to variable span nodes.
  const decoded = normalized.replace(/&lt;&lt;([^&]+)&gt;&gt;/g, "<<$1>>");
  const variableRegex = /<<([^>]+)>>/g;
  return decoded.replace(variableRegex, (match) => {
    return `<span data-variable="${match}" class="variable-node"></span>`;
  });
};

export const convertVariableNodesToText = (html: string): string => {
  return html
    .replace(
      /<span[^>]*data-variable="([^"]+)"[^>]*>[^<]*<\/span>/g,
      (_m, v: string) => v.replace(/<<([^>]+)>>/g, (_m2, k: string) => `<<${k.toLowerCase()}>>`),
    )
    .replace(/&lt;&lt;([^&]+)&gt;&gt;/g, (_m, k: string) => `<<${k.toLowerCase()}>>`)
    .replace(/<<([^>]+)>>/g, (_m, k: string) => `<<${k.toLowerCase()}>>`)
    .replace(/<\/?p>/g, "")
    .trim();
};

export const convertVariableNodesToPlaceholders = (html: string): string => {
  // First, replace any variable span nodes with the raw placeholder from data-variable
  let result = html.replace(
    /<span[^>]*data-variable="([^"]+)"[^>]*>[^<]*<\/span>/g,
    (_m, v: string) => v.replace(/<<([^>]+)>>/g, (_m2, k: string) => `<<${k.toLowerCase()}>>`),
  );
  // TipTap's getHTML() HTML-encodes angle brackets in text content, so
  // <<key>> becomes &lt;&lt;key&gt;&gt;.  Decode them back so the MFE
  // receives raw <<key>> placeholders it can resolve.
  // Also lowercase the keys — the SendIt MFE uses case-insensitive matching.
  result = result.replace(/&lt;&lt;([^&]+)&gt;&gt;/g, (_m, k: string) => `<<${k.toLowerCase()}>>`)
  result = result.replace(/<<([^>]+)>>/g, (_m, k: string) => `<<${k.toLowerCase()}>>`)
  return result;
};

/** Replace all `<<key>>` placeholders in an HTML string with values from the
 *  supplied data map.  Also handles HTML-encoded variants (`&lt;&lt;key&gt;&gt;`)
 *  that TipTap may produce.  Keys that don't have a matching value are left as-is. */
export const resolvePlaceholders = (
  html: string,
  data: Record<string, string>,
): string => {
  if (!html) return html;
  // Resolve raw <<key>>
  let result = html.replace(/<<([^>]+)>>/g, (match, key: string) => {
    const trimmed = key.trim();
    if (trimmed in data) return data[trimmed];
    const wrapped = `<<${trimmed}>>`;
    if (wrapped in data) return data[wrapped];
    return match;
  });
  // Resolve HTML-encoded &lt;&lt;key&gt;&gt;
  result = result.replace(/&lt;&lt;([^&]+)&gt;&gt;/g, (match, key: string) => {
    const trimmed = key.trim();
    if (trimmed in data) return data[trimmed];
    return match;
  });
  return result;
};


export const isValidEmail = (email: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

/** Extract all unique `<<key>>` placeholder keys from an HTML string.
 *  Also matches HTML-encoded variants (`&lt;&lt;key&gt;&gt;`) that TipTap may produce. */
export function extractPlaceholderKeys(html: string): string[] {
  if (!html) return [];
  const keys = new Set<string>();

  // Match raw <<key>>
  const rawRegex = /<<([^>]+)>>/g;
  let match: RegExpExecArray | null;
  while ((match = rawRegex.exec(html)) !== null) {
    keys.add(match[1].trim());
  }

  // Match HTML-encoded &lt;&lt;key&gt;&gt;
  const encodedRegex = /&lt;&lt;([^&]+)&gt;&gt;/g;
  while ((match = encodedRegex.exec(html)) !== null) {
    keys.add(match[1].trim());
  }

  return [...keys];
}

/** Return placeholder keys present in the HTML that are NOT resolvable by the given merge data map. */
export function findUnresolvedPlaceholders(
  html: string,
  mergeData: Record<string, string>,
): string[] {
  const keys = extractPlaceholderKeys(html);
  return keys.filter((key) => !(key in mergeData));
}

/**
 * Returns placeholder keys found in the HTML that are NOT in the predefined
 * set of available template variables.  Used to hard-block saving templates
 * that contain unknown / mistyped variables.
 *
 * @param html          The template HTML (subject + body).
 * @param allowedKeys   Set of allowed inner keys (without braces), e.g. "customerName".
 */
export function findUnknownVariables(
  html: string,
  allowedKeys: Set<string>,
): string[] {
  const keys = extractPlaceholderKeys(html);
  return keys.filter((key) => !allowedKeys.has(key));
}

// ─── Uploaded file type ──────────────────────────────────────────────────────

export interface UploadedFile {
  name: string;
  dataUrl: string;
  contentType: string;
  base64Content: string;
}

// ─── Placeholder context & builder ──────────────────────────────────────────

export interface PlaceholderCustomerData {
  name: string;
  email: string;
  phone?: string;
  accountNumber: string;
  outstanding?: number;
  overdue?: number;
  creditLimit?: number;
  currency?: string;
}

export interface PlaceholderTransactionData {
  referenceNumber: string;
  date: string;
  dueDate?: string;
  amount: number;
  outstandingAmount?: number;
  currency: string;
  daysOverdue?: number;
}

export interface PlaceholderContactData {
  firstName: string;
  surname: string;
  email?: string;
  phone?: string;
}

export interface PlaceholderCreditControllerData {
  firstName: string;
  lastName: string;
  email: string;
}

export interface PlaceholderCompanyData {
  name: string;
  email?: string;
  phone?: string;
  supportEmail?: string;
}

export interface PlaceholderContext {
  recipientEmail: string;
  customer?: PlaceholderCustomerData;
  contact?: PlaceholderContactData;
  transaction?: PlaceholderTransactionData;
  creditController?: PlaceholderCreditControllerData;
  company?: PlaceholderCompanyData;
}

function formatCurrency(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

// formatDate is intentionally kept for future use by callers that resolve
// transaction-level placeholders outside of the template editor.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

/**
 * Builds a comprehensive placeholder-value map using camelCase keys
 * to match the predefined template variables.
 *
 * The SendIt MFE resolves <<key>> placeholders in template.subject / template.body
 * using each recipient's mergeData dictionary.  This function builds that dictionary.
 * Values that are not available are omitted — any remaining unresolved
 * placeholders will be caught by `findUnresolvedPlaceholders` in the send
 * modals and the user will be asked to remove them.
 *
 * @deprecated Use the server-side merge data API via `useEmailMergeData` hook instead.
 * This function is kept during the transition period and will be removed once all
 * callers are migrated and verified.
 */
export function buildFullMergeData(ctx: PlaceholderContext): Record<string, string> {
  const data: Record<string, string> = {};
  const { customer, contact, creditController, company } = ctx;

  // Note: recipientEmail and transaction fields are kept in the context interface
  // for backward compatibility with callers (send email modals) but the
  // corresponding template variables have been removed from the template system.

  // ── Customer ───────────────────────────────────────────────────────
  if (customer) {
    const cur = customer.currency ?? "GBP";

    data["customerName"] = customer.name;
    data["customerEmail"] = customer.email;

    if (customer.phone) {
      data["customerPhone"] = customer.phone;
    }

    data["accountNumber"] = customer.accountNumber;

    if (customer.outstanding !== undefined) {
      data["accountBalance"] = formatCurrency(customer.outstanding, cur);
    }

    if (customer.overdue !== undefined) {
      data["overdueAmount"] = formatCurrency(customer.overdue, cur);
    }

    if (customer.creditLimit !== undefined) {
      data["creditLimit"] = formatCurrency(customer.creditLimit, cur);
    }
  }

  // ── Contact ─────────────────────────────────────────────────────────
  if (contact) {
    data["contactFirstName"] = contact.firstName;
    data["contactSurname"] = contact.surname;

    if (contact.email) {
      data["contactEmail"] = contact.email;
    }

    if (contact.phone) {
      data["contactPhone"] = contact.phone;
    }
  }

  // ── Credit Controller ──────────────────────────────────────────────
  if (creditController) {
    data["creditController"] = `${creditController.firstName} ${creditController.lastName}`;
    data["creditControllerFirstName"] = creditController.firstName;
    data["creditControllerLastName"] = creditController.lastName;
    data["creditControllerEmail"] = creditController.email;
  }

  // ── Company ────────────────────────────────────────────────────────
  if (company) {
    data["companyName"] = company.name;

    if (company.email) {
      data["companyEmail"] = company.email;
    }

    if (company.phone) {
      data["companyPhone"] = company.phone;
    }

    data["supportEmail"] = company.supportEmail ?? "";
  }

  // ── System ─────────────────────────────────────────────────────────
  data["systemDate"] = new Date().toLocaleDateString("en-GB");
  data["systemTime"] = new Date().toLocaleTimeString("en-GB");

  return data;
}
